import { CategoryForm } from '@/components/categories/CategoryForm'
import { CategoryList } from '@/components/categories/CategoryList'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import React, { useCallback, useEffect, useState } from 'react'

type CategoryFormData = {
  name: string
  description?: string
  status: 'active' | 'inactive'
  sortOrder: number
  parentId?: string
  seoTitle?: string
  seoDescription?: string
  seoKeywords?: string
}

type CategoryListItem = {
  id: string
  name: string
  slug: string
  description: string
  status: 'active' | 'inactive'
  productCount: number
  createdAt: string
  parent_id?: string
  parent?: {
    id: string
    name: string
  }
  children?: CategoryListItem[]
  display_order?: number
}

interface CategoryApiData {
  id: string
  name: string
  slug?: string
  description?: string
  status?: 'active' | 'inactive'
  is_active?: boolean
  product_count?: number
  product_counts?: { total?: number }
  products_count?: number
  created_at?: string | Date
  parent_id?: string
  parent?: {
    id: string
    name: string
  }
  children?: CategoryApiData[]
  display_order?: number
}

// Helper function to convert CategoryApiData to CategoryListItem
const convertToCategoryListItem = (category: CategoryApiData): CategoryListItem => {
  const status = category.status || (category.is_active ? 'active' : 'inactive')
  const productCount =
    category.product_count || category.product_counts?.total || category.products_count || 0

  let createdAt = ''
  const createdDate = category.created_at
  if (createdDate) {
    try {
      const date = new Date(createdDate as string | number | Date)
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        createdAt = `${day}-${month}-${year}`
      } else if (typeof createdDate === 'string' && createdDate.length >= 10) {
        const dateStr = createdDate.substring(0, 10)
        const parts = dateStr.split('-')
        if (parts.length === 3) {
          createdAt = `${parts[2]}-${parts[1]}-${parts[0]}`
        } else {
          createdAt = dateStr
        }
      } else {
        createdAt = String(createdDate)
      }
    } catch {
      if (typeof createdDate === 'string' && createdDate.length >= 10) {
        const dateStr = createdDate.substring(0, 10)
        const parts = dateStr.split('-')
        if (parts.length === 3) {
          createdAt = `${parts[2]}-${parts[1]}-${parts[0]}`
        } else {
          createdAt = dateStr
        }
      } else {
        createdAt = String(createdDate)
      }
    }
  }

  return {
    id: category.id,
    name: category.name,
    slug: category.slug || '',
    description: category.description || '',
    status: status as 'active' | 'inactive',
    productCount,
    createdAt,
    parent_id: category.parent_id,
    parent: category.parent,
    children: category.children ? category.children.map(convertToCategoryListItem) : [],
    display_order: category.display_order,
  }
}

const CategoriesPage: React.FC = () => {
  const { toast } = useToast()
  const [showForm, setShowForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Partial<CategoryFormData> | undefined>(
    undefined
  )
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [categories, setCategories] = useState<CategoryListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [lastPage, setLastPage] = useState(1)

  // New state for delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<CategoryListItem | null>(null)

  const loadCategories = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.getCategories({
        page: currentPage,
        per_page: perPage,
      })

      const categoriesData: CategoryApiData[] = Array.isArray(response.data) ? response.data : []
      const resolvedTotal = response.meta?.total ?? categoriesData.length
      const resolvedLastPage =
        response.meta?.last_page ?? Math.max(1, Math.ceil(resolvedTotal / perPage))

      setTotal(resolvedTotal)
      setLastPage(resolvedLastPage)

      const mappedCategories: CategoryListItem[] = categoriesData.map(convertToCategoryListItem)
      setCategories(mappedCategories)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load categories. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast, currentPage, perPage])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  const handleAddCategory = () => {
    setEditingCategory(undefined)
    setShowForm(true)
  }

  const handleFormSubmit = async (data: CategoryFormData) => {
    setIsSubmitting(true)
    try {
      // Generate slug from name if not editing
      const generateSlug = (name: string): string => {
        return name
          .toLowerCase()
          .trim()
          .replace(/[^\w\s-]/g, '')
          .replace(/[\s_-]+/g, '-')
          .replace(/^-+|-+$/g, '')
      }

      const payload = {
        name: data.name,
        slug: generateSlug(data.name),
        description: data.description || undefined,
        is_active: data.status === 'active',
        parent_id: data.parentId || undefined,
        display_order: data.sortOrder,
        meta_title: data.seoTitle || undefined,
        meta_description: data.seoDescription || undefined,
        meta_keywords: data.seoKeywords || undefined,
      }

      if (editingCategoryId) {
        await apiClient.updateCategory(editingCategoryId, payload)
      } else {
        await apiClient.createCategory(payload)
      }

      toast({
        title: 'Success',
        description: editingCategoryId
          ? 'Category updated successfully'
          : 'Category created successfully',
      })

      setShowForm(false)
      setEditingCategory(undefined)
      setEditingCategoryId(null)
      setCurrentPage(1)
      loadCategories()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save category. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setEditingCategory(undefined)
    setEditingCategoryId(null)
  }

  const handleDeleteClick = (categoryId: string) => {
    // Find category in all categories, not just the filtered ones
    const findCategory = (cats: CategoryListItem[], id: string): CategoryListItem | null => {
      for (const cat of cats) {
        if (cat.id === id) return cat
        if (cat.children) {
          const found = findCategory(cat.children, id)
          if (found) return found
        }
      }
      return null
    }

    const category = findCategory(categories, categoryId)
    if (category) {
      setCategoryToDelete(category)
      setShowDeleteDialog(true)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return

    try {
      // Use bulk update with delete action and move products to Uncategorized
      await apiClient.bulkUpdateCategories({
        category_ids: [categoryToDelete.id],
        action: 'delete',
        data: {
          move_products_to: '00000000-0000-0000-0000-000000000001', // Uncategorized category ID
        },
      })

      toast({
        title: 'Success',
        description: 'Category deleted successfully. Products moved to Uncategorized.',
      })

      setShowDeleteDialog(false)
      setCategoryToDelete(null)
      setCurrentPage(1)
      loadCategories()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete category. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleToggleCategoryStatus = async (
    categoryId: string,
    currentStatus: 'active' | 'inactive'
  ) => {
    try {
      const newStatus = currentStatus === 'active' ? false : true
      await apiClient.toggleCategoryStatus(categoryId, newStatus)

      toast({
        title: 'Success',
        description: `Category ${newStatus ? 'activated' : 'deactivated'} successfully`,
      })

      loadCategories()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update category status. Please try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Categories</h1>
        <p className="text-muted-foreground">
          Manage product categories and hierarchical organization
        </p>
      </div>

      {showForm ? (
        <CategoryForm
          initialData={editingCategory}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={isSubmitting}
          editingCategoryId={editingCategoryId || undefined}
        />
      ) : (
        <CategoryList
          className="w-full"
          categories={categories} // Use paginated categories instead of allCategories
          isLoading={isLoading}
          currentPage={currentPage}
          total={total}
          lastPage={lastPage}
          perPage={perPage}
          onPageChange={setCurrentPage}
          onAddCategory={handleAddCategory}
          onDeleteCategory={handleDeleteClick}
          onToggleStatus={handleToggleCategoryStatus}
          onRefresh={loadCategories}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        _open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Category"
        description="Are you sure you want to delete this category? This action cannot be undone."
        details={
          categoryToDelete
            ? `${categoryToDelete.name} (has ${categoryToDelete.productCount} products)`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />
    </div>
  )
}

export default CategoriesPage
